import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Image,
  Text
} from '@chakra-ui/react';
import { useVendor, DisplayType } from '@outdoorly/react-chat-sdk';
import React, { useEffect, useMemo, useState } from 'react';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { EXPERT_IMAGES } from '../../constants';
import { activatorVisibleKey, Store } from '../../store';
import { MotionBox, MotionFlex } from '../Motion';
import activatorImage from './activator-icon.png';
import { CloseIcon, Vetted } from '../../icons';
import { useDimensions } from '../../utils/dimensions';
import posthog from 'posthog-js';
import { IntroductionMessage } from '../IntroSequence/IntroductionMessage';

export const openWidget = () => {
  Store.update((s) => {
    s.activatorVisible = false;
    s.unseenMessages = 0;
  });
  localStorage.setItem('remark-unseenRemarkMessages', '0');
  // TODO, delete after 1/27/22, replace getItem instances
  localStorage.setItem('unseenRemarkMessages', '0');
  localStorage.setItem(activatorVisibleKey, 'hidden');
  // TODO, delete after 1/27/22
  localStorage.setItem('isActivatorVisible', 'hidden');
};

export const Activator = React.memo(() => {
  const isOpen = Store.useState((s) => s.activatorVisible);
  const { vendor } = useVendor();
  const showActivatorCard = useMemo(() => vendor?.showActivator ?? DisplayType.All, [vendor]);
  const [activatorCardHidden, setActivatorCardHidden] = useState(
    localStorage.getItem('isActivatorCardHidden') === 'hidden' ?? false
  );

  const { isDesktop, ...dims } = useDimensions();
  const isMicroScreen = useMemo(() => dims.width < 350, [dims.width]);

  useEffect(() => {
    if (
      showActivatorCard === DisplayType.None ||
      (showActivatorCard === DisplayType.Mobile && isDesktop) ||
      (showActivatorCard === DisplayType.Desktop && !isDesktop)
    ) {
      setActivatorCardHidden(true);
    }
  }, [showActivatorCard, isDesktop]);

  useEffect(() => {
    if (isMicroScreen && !activatorCardHidden) {
      setActivatorCardHidden(true);
    } else if (
      !isMicroScreen &&
      activatorCardHidden &&
      !Boolean(localStorage.getItem('isActivatorCardHidden'))
    ) {
      setActivatorCardHidden(false);
    }
  }, [isMicroScreen]);

  const labels = Store.useState((s) => s.labels);
  const unreadMessages = Store.useState((s) => s.unseenMessages);
  return (
    <>
      {isOpen &&
        (isDesktop || activatorCardHidden ? (
          <MotionFlex
            key="desktop-activator"
            direction="row"
            align="end"
            justify="end"
            marginBottom="20px"
            marginRight="20px"
            // Put the activator inside the single grid cell, so we can underlap the chat window
            gridArea={'a'}
            marginTop="10px"
            marginLeft="10px"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {!activatorCardHidden && (
              <MotionBox
                minW="294px"
                maxW="294px"
                position="relative"
                initial={{ display: 'none', translateY: 10, opacity: 0 }}
                animate={{ display: 'block', translateY: 0, opacity: 1, transition: { delay: 2 } }}
                exit={{ translateY: 10, opacity: 0 }}
                marginRight="10px"
              >
                <IconButton
                  aria-label="close-additional-info-card"
                  icon={<CloseIcon width="8px" fill="scheme.contentTertiary" />}
                  variant="close-activator-info-button"
                  position="absolute"
                  tabIndex={-1}
                  left="-8px"
                  top="-8px"
                  onClick={() => {
                    posthog.capture('CLOSE-ACTIVATOR-EXPLAINER');
                    setActivatorCardHidden(true);
                    localStorage.setItem('isActivatorCardHidden', 'hidden');
                  }}
                />
                <Box
                  p="12px"
                  background="scheme.backgroundPrimary"
                  borderRadius="8px"
                  w="100%"
                  m="auto"
                  onClick={() => {
                    posthog.capture('OPEN-WIDGET', { activationSource: 'floating-explainer' });
                    openWidget();
                  }}
                  cursor="pointer"
                >
                  <ExpertActivator />
                </Box>
              </MotionBox>
            )}
            <MotionBox
              animate={{ scale: [1, 1.1, 1], transition: { repeat: Infinity, duration: 4 } }}
              minW="60px"
              minH="60px"
              as="button"
              borderRadius="50%"
              aria-label="open Expert Help chat"
              cursor="pointer"
              _focusVisible={{ outline: '1px solid orange' }}
              backgroundColor="scheme.whiteCompatible"
              pointerEvents="all"
              onClick={() => {
                posthog.capture('OPEN-WIDGET', { activationSource: 'floating-button' });
                openWidget();
              }}
            >
              {unreadMessages > 0 && (
                <Badge
                  top={0}
                  right={0}
                  background="scheme.activatorButtonPrimary"
                  borderRadius="50%"
                  pos="absolute"
                  color="scheme.contentInversePrimary"
                  minH="16px"
                  minW="16px"
                >
                  <Text variant="label-medium" textAlign="center">
                    {unreadMessages}
                  </Text>
                </Badge>
              )}
              <Center>
                <MotionBox>
                  <Image
                    src={activatorImage}
                    width="60px"
                    height="60px"
                    userSelect="none"
                    draggable={false}
                    role="presentation"
                  />
                </MotionBox>
              </Center>
            </MotionBox>
          </MotionFlex>
        ) : (
          <MotionFlex
            key="mobile-activator"
            direction="column"
            marginBottom="20px"
            // If we're mobile, this should attempt to fill the whole width of the browser
            w={dims.width}
            // Put the activator inside the single grid cell, so we can underlap the chat window
            gridArea="a"
            align="start"
            justify="end"
            initial={{ translateY: 10, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            exit={{ translateY: 10, opacity: 0 }}
          >
            <Button
              variant="chat-option"
              ml="20px"
              mb="4px"
              background="scheme.backgroundPrimary"
              _hover={{
                '& span > svg': {
                  stroke: 'scheme.contentPrimary'
                }
              }}
              _active={{
                '& span > svg': {
                  stroke: 'scheme.contentPrimary'
                }
              }}
              onClick={() => {
                posthog.capture('CLOSE-ACTIVATOR-EXPLAINER');
                setActivatorCardHidden(true);
                localStorage.setItem('isActivatorCardHidden', 'hidden');
              }}
              leftIcon={<CloseIcon w="8px" fill="scheme.contentTertiary" />}
            >
              {labels.closeActivator}
            </Button>
            <Box
              p="12px"
              background="scheme.backgroundPrimary"
              borderRadius="8px"
              maxW="calc(100% - 40px)"
              w="calc(100% - 40px)"
              marginX="auto"
              onClick={() => {
                posthog.capture('OPEN-WIDGET', { activationSource: 'floating-explainer-mobile' });
                openWidget();
              }}
              cursor="pointer"
            >
              <Flex align="center" justify="space-between">
                <ExpertActivator />
                <Center>
                  <Box
                    display="block"
                    backgroundColor="scheme.whiteCompatible"
                    borderRadius="50%"
                    ml="8px"
                    pos="relative"
                  >
                    <Image
                      src={activatorImage}
                      width="60px"
                      height="60px"
                      minW="60px"
                      userSelect="none"
                      draggable={false}
                      role="presentation"
                    />
                    {unreadMessages > 0 && (
                      <Badge
                        top={0}
                        right={0}
                        background="scheme.activatorButtonPrimary"
                        borderRadius="50%"
                        pos="absolute"
                        color="scheme.contentInversePrimary"
                        minH="16px"
                        minW="16px"
                      >
                        <Text variant="label-medium" textAlign="center">
                          {unreadMessages}
                        </Text>
                      </Badge>
                    )}
                  </Box>
                </Center>
              </Flex>
            </Box>
          </MotionFlex>
        ))}
    </>
  );
});

const ExpertActivator = React.memo(() => {
  return (
    <Box maxW="270px">
      <IntroductionMessage showActivateChatButton={false} />
    </Box>
  );
});
